import React from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import useWaitlistStats from "@/hooks/useWaitlistStats";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1773393799115-a79117dd3194?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjYXIlMjBkdWJhaSUyMHNreWxpbmV8ZW58MHx8fHwxNzgyMzI5Njk4fDA&ixlib=rb-4.1.0&q=85";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
  const { total, hosts, renters, loading } = useWaitlistStats(15000);
  const fmt = (n) => (loading ? "—" : n.toLocaleString("en-US"));

  return (
    <section
      data-testid="hero-section"
      className="relative pt-28 sm:pt-36 lg:pt-44 pb-0 overflow-hidden bg-white"
    >
      {/* Hairline grid */}
      <div className="absolute inset-0 bg-hairline pointer-events-none [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_70%)]" />
      {/* Soft red halo */}
      <div className="absolute -top-40 right-[-12rem] w-[640px] h-[640px] rounded-full bg-carvny-red/[0.07] blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Editorial top bar */}
        <div className="fade-up flex items-center justify-between text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.28em] text-carvny-ink/60">
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-carvny-red" />
            Dubai · UAE
          </span>
          <span className="hidden sm:inline">Vol. 01 — Pre-Launch Edition</span>
          <span>2026</span>
        </div>
        <div className="rule mt-5" />

        {/* Headline grid */}
        <div className="mt-12 sm:mt-16 lg:mt-20 grid grid-cols-12 gap-y-10 lg:gap-x-12">
          {/* Left rail / eyebrow */}
          <div className="col-span-12 lg:col-span-3 lg:pt-3">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-carvny-red">
              The Marketplace
            </div>
            <p className="mt-4 text-[13px] leading-relaxed text-carvny-muted max-w-[18rem]">
              Dubai&apos;s first peer-to-peer car rental built on three
              guardrails: frozen-hold deposits, 360° check-in, live fine
              tracking.
            </p>
          </div>

          {/* Headline */}
          <h1
            data-testid="hero-headline"
            className="col-span-12 lg:col-span-9 font-display font-black tracking-tightest text-carvny-ink leading-[0.92] text-[48px] xs:text-[58px] sm:text-[76px] lg:text-[104px] xl:text-[120px]"
          >
            <span className="block">Your car.</span>
            <span className="block">
              Their <span className="font-serif-italic text-carvny-ink/80 font-normal">journey</span>.
            </span>
            <span className="block text-carvny-red">Everyone wins.</span>
          </h1>
        </div>

        {/* Sub + CTAs row */}
        <div className="mt-12 sm:mt-14 grid grid-cols-12 gap-y-8 lg:gap-x-12 items-end">
          <p
            data-testid="hero-subheadline"
            className="col-span-12 lg:col-span-6 text-lg sm:text-xl leading-relaxed text-carvny-muted max-w-2xl"
          >
            Dubai&apos;s first trusted peer-to-peer car rental marketplace.
            Built so owners get paid and renters get the keys —
            <span className="font-serif-italic text-carvny-ink"> without the deposit games</span>,
            inspection disputes, or hidden RTA surprises.
          </p>

          <div className="col-span-12 lg:col-span-6 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 sm:gap-4">
            <button
              data-testid="hero-list-car-cta"
              onClick={() => scrollTo("waitlist")}
              className="group inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 rounded-full text-base font-bold bg-carvny-red text-white hover:bg-carvny-red-hover transition-all shadow-[0_18px_40px_-14px_rgba(204,0,0,0.55)]"
            >
              Host Your Car
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-find-car-cta"
              onClick={() => scrollTo("waitlist")}
              className="group inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 rounded-full text-base font-bold bg-white text-carvny-ink border border-carvny-ink hover:bg-carvny-ink hover:text-white transition-colors"
            >
              Rent a Car
              <ArrowUpRight size={18} className="transition-transform group-hover:rotate-12" />
            </button>
          </div>
        </div>

        {/* Cinematic image — letterbox premium feel */}
        <div className="mt-16 sm:mt-20 lg:mt-24 fade-up delay-3 relative">
          <div className="relative overflow-hidden rounded-[24px] sm:rounded-[32px] border border-carvny-border noise">
            <img
              src={HERO_IMAGE}
              alt="A luxury red sports car cruising Downtown Dubai past Burj Khalifa"
              loading="eager"
              className="w-full h-[320px] sm:h-[500px] lg:h-[640px] object-cover"
            />
            {/* Vignette */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/0" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-transparent" />

            {/* Caption — bottom left */}
            <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10 lg:p-12">
              <div className="flex items-end justify-between gap-6">
                <div className="text-white max-w-md">
                  <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.28em] opacity-80">
                    On the cover · Downtown Dubai
                  </div>
                  <div className="mt-3 font-display text-2xl sm:text-4xl font-bold leading-[1.05]">
                    Every drive,
                    <span className="font-serif-italic font-normal opacity-90"> fully accountable.</span>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-2 text-white/90 text-[11px] font-bold uppercase tracking-[0.24em]">
                  <span className="relative w-2 h-2 rounded-full bg-carvny-red pulse-ring" />
                  Live system
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Editorial mini-row beneath image — live counters */}
        <div className="mt-10 sm:mt-12" data-testid="hero-stats-row">
          <div className="rule" />
          <div className="grid grid-cols-3 divide-x divide-carvny-border">
            <StatBlock
              kicker="On the waitlist"
              value={fmt(total)}
              caption="And counting"
              testid="hero-stat-total"
            />
            <StatBlock
              kicker="Hosts"
              value={fmt(hosts)}
              caption="Cars pre-listed"
              testid="hero-stat-hosts"
            />
            <StatBlock
              kicker="Renters"
              value={fmt(renters)}
              caption="Awaiting launch"
              testid="hero-stat-renters"
            />
          </div>
          <div className="rule" />
        </div>
      </div>
    </section>
  );
}

function StatBlock({ kicker, value, caption, testid }) {
  return (
    <div
      data-testid={testid}
      className="px-3 sm:px-6 py-6 first:pl-0 last:pr-0"
    >
      <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.24em] text-carvny-red">
        {kicker}
      </div>
      <div className="mt-2 font-display text-3xl sm:text-5xl font-black text-carvny-ink leading-none tracking-tightest tabular-nums">
        {value}
      </div>
      <div className="mt-2 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-carvny-muted">
        {caption}
      </div>
    </div>
  );
}
